import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck,
  FileKey,
  Plus,
  Upload,
  Download,
  Trash2,
  MoreVertical,
  Check,
  X,
  AlertTriangle,
  Building2,
  Globe,
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  FileCheck,
  AlertCircle,
  Eye,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { generateUUID } from "@/utils/transform";
import { certificatesApi } from "../../services/api";

export const CertificatesSettings = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("certificates");
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showP12UploadDialog, setShowP12UploadDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showCertDetailsDialog, setShowCertDetailsDialog] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [verificationFile, setVerificationFile] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const verifyFileRef = useRef(null);

  // Fetch settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["certificate-settings"],
    queryFn: () => certificatesApi.getSettings().then((res) => res.data?.data),
  });

  // Fetch certificates
  const { data: certsData, isLoading: certsLoading } = useQuery({
    queryKey: ["certificates"],
    queryFn: () => certificatesApi.list().then((res) => res.data?.data),
  });

  // Fetch expiring certificates
  const { data: expiringCerts } = useQuery({
    queryKey: ["expiring-certificates"],
    queryFn: () =>
      certificatesApi.getExpiring(30).then((res) => res.data?.data),
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (data) => certificatesApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificate-settings"]);
      toast.success("Settings updated");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to update settings",
      );
    },
  });

  // Generate certificate mutation
  const generateMutation = useMutation({
    mutationFn: (data) => certificatesApi.generate(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificates"]);
      setShowGenerateDialog(false);
      toast.success("Certificate generated successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          "Failed to generate certificate",
      );
    },
  });

  // Upload PEM certificate mutation
  const uploadMutation = useMutation({
    mutationFn: (data) => certificatesApi.upload(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificates"]);
      setShowUploadDialog(false);
      toast.success("Certificate uploaded successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to upload certificate",
      );
    },
  });

  // Upload P12 certificate mutation
  const uploadP12Mutation = useMutation({
    mutationFn: (formData) => certificatesApi.uploadP12(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificates"]);
      setShowP12UploadDialog(false);
      toast.success("P12 certificate uploaded successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          "Failed to upload P12 certificate",
      );
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id) => certificatesApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificates"]);
      toast.success("Default certificate updated");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to set default",
      );
    },
  });

  // Revoke mutation
  const revokeMutation = useMutation({
    mutationFn: ({ id, reason }) => certificatesApi.revoke(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificates"]);
      setShowRevokeDialog(false);
      setSelectedCert(null);
      setRevokeReason("");
      toast.success("Certificate revoked");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to revoke certificate",
      );
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => certificatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["certificates"]);
      toast.success("Certificate deleted");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to delete certificate",
      );
    },
  });

  const handleToggleDigitalSignatures = (checked) => {
    updateSettingsMutation.mutate({ digitalSignatureEnabled: checked });
  };

  const handleDownloadCertificate = async (cert) => {
    try {
      const response = await certificatesApi.download(cert.id);
      const blob = new Blob([response.data], {
        type: "application/x-pem-file",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cert.common_name.replace(
        /[^a-z0-9]/gi,
        "_",
      )}_certificate.pem`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Certificate downloaded");
    } catch {
      toast.error("Failed to download certificate");
    }
  };

  const handleVerifyDocument = async () => {
    if (!verificationFile) {
      toast.error("Please select a PDF file to verify");
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      const formData = new FormData();
      formData.append("file", verificationFile);
      const response = await certificatesApi.verify(formData);
      setVerificationResult(response.data?.data);
    } catch (error) {
      toast.error(
        error.response?.data?.error?.message || "Verification failed",
      );
      setVerificationResult({
        status: "error",
        message: error.response?.data?.error?.message || "Verification failed",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "valid":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "invalid":
      case "tampered":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "expired_cert":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case "no_signatures":
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "valid":
        return "bg-green-50 border-green-200 text-green-800";
      case "invalid":
      case "tampered":
        return "bg-red-50 border-red-200 text-red-800";
      case "expired_cert":
        return "bg-yellow-50 border-yellow-200 text-yellow-800";
      default:
        return "bg-gray-50 border-gray-200 text-gray-800";
    }
  };

  if (settingsLoading || certsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const certificates = certsData?.certificates || [];
  const platformCert = certsData?.platformCertificate;

  const renderCertificateWrapperClass = (cert) => {
    if (cert?.revoked_at) {
      return "bg-gray-50 border-gray-200";
    }
    if (cert?.isExpired) {
      return "bg-red-50 border-red-200";
    }
    return cert?.isExpiringSoon
      ? "bg-yellow-50 border-yellow-200"
      : "bg-white border-gray-200";
  };

  const renderCertificateIconWrapperClass = (cert) => {
    if (cert?.revoked_at) {
      return "bg-gray-100";
    }
    return cert?.isExpired ? "bg-red-100" : "bg-green-100";
  };

  const renderCertificateIconClass = (cert) => {
    if (cert?.revoked_at) {
      return "text-gray-400";
    }
    return cert?.isExpired ? "text-red-600" : "text-green-600";
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Digital Signatures
          </h1>
          <p className="text-muted-foreground">
            Manage signing certificates and verify document signatures
          </p>
        </div>
      </div>

      {/* Expiring Certificates Warning */}
      {expiringCerts?.length > 0 && (
        <Alert variant="destructive" className="border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800">
            Certificate Expiring Soon
          </AlertTitle>
          <AlertDescription className="text-orange-700">
            {expiringCerts.length} certificate(s) will expire within 30 days.
            {expiringCerts.map((cert) => (
              <span key={cert.id} className="block text-sm mt-1">
                • {cert.common_name} expires on{" "}
                {new Date(cert.valid_to).toLocaleDateString()}
              </span>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <FileKey className="h-4 w-4" />
            Certificates
          </TabsTrigger>
          <TabsTrigger value="verify" className="flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            Verify Document
          </TabsTrigger>
        </TabsList>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-6">
          {/* Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Digital Signature Settings
              </CardTitle>
              <CardDescription>
                Configure how digital signatures are applied to your documents
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Digital Signatures</Label>
                  <p className="text-sm text-gray-500">
                    Automatically apply cryptographic signatures to completed
                    documents
                  </p>
                </div>
                <Switch
                  checked={settings?.digitalSignatureEnabled ?? true}
                  onCheckedChange={handleToggleDigitalSignatures}
                />
              </div>
            </CardContent>
          </Card>

          {/* Platform Certificate */}
          {platformCert?.isAvailable && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-500" />
                  Platform Certificate
                </CardTitle>
                <CardDescription>
                  Default certificate provided by eScribe for all organisations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <ShieldCheck className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {platformCert.commonName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {platformCert.organisation}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Valid until{" "}
                        {new Date(platformCert.validTo).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-700"
                  >
                    Active
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  This certificate is automatically used unless you configure an
                  organisation-specific certificate below.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Organisation Certificates */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Organization Certificates
                  </CardTitle>
                  <CardDescription>
                    Upload your own CA-issued certificate for branded signatures
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Certificate
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => setShowGenerateDialog(true)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Generate Self-Signed
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setShowUploadDialog(true)}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload PEM Certificate
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowP12UploadDialog(true)}
                      >
                        <FileKey className="h-4 w-4 mr-2" />
                        Upload P12/PFX Certificate
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {certificates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileKey className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No organisation certificates configured</p>
                  <p className="text-sm">
                    The platform certificate will be used for signing
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {certificates.map((cert) => (
                    <div
                      key={cert.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${renderCertificateWrapperClass(
                        cert,
                      )}`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-2 rounded-lg ${renderCertificateIconWrapperClass(cert)}`}
                        >
                          <FileKey
                            className={`h-6 w-6 ${renderCertificateIconClass(cert)}`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {cert.common_name}
                            </p>
                            {cert.is_default && (
                              <Badge variant="secondary" className="text-xs">
                                Default
                              </Badge>
                            )}
                            {cert.certificate_type === "ca_issued" && (
                              <Badge variant="outline" className="text-xs">
                                CA Issued
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {cert.organisation_name || "Self-signed"}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Expires:{" "}
                            {new Date(cert.valid_to).toLocaleDateString()}
                            {cert.revoked_at && (
                              <span className="text-red-600 ml-2">
                                (Revoked: {cert.revoke_reason})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {cert.isExpiringSoon &&
                          !cert.isExpired &&
                          !cert.revoked_at && (
                            <Badge
                              variant="outline"
                              className="bg-yellow-100 text-yellow-700"
                            >
                              Expiring Soon
                            </Badge>
                          )}
                        {cert.isExpired && (
                          <Badge variant="destructive">Expired</Badge>
                        )}
                        {cert.revoked_at && (
                          <Badge variant="secondary">Revoked</Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedCert(cert);
                                setShowCertDetailsDialog(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDownloadCertificate(cert)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download Public Key
                            </DropdownMenuItem>
                            {!cert.is_default &&
                              !cert.revoked_at &&
                              !cert.isExpired && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setDefaultMutation.mutate(cert.id)
                                  }
                                >
                                  <Check className="h-4 w-4 mr-2" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                            <DropdownMenuSeparator />
                            {!cert.revoked_at && (
                              <DropdownMenuItem
                                className="text-orange-600"
                                onClick={() => {
                                  setSelectedCert(cert);
                                  setShowRevokeDialog(true);
                                }}
                              >
                                <X className="h-4 w-4 mr-2" />
                                Revoke
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => deleteMutation.mutate(cert.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verify Document Tab */}
        <TabsContent value="verify" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Verify Document Signature
              </CardTitle>
              <CardDescription>
                Upload a signed PDF to verify its digital signature and document
                integrity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-gray-300 cursor-pointer transition-colors"
                onClick={() => verifyFileRef.current?.click()}
              >
                <input
                  ref={verifyFileRef}
                  aria-label="Upload Signed PDF"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => {
                    setVerificationFile(e.target.files?.[0] || null);
                    setVerificationResult(null);
                  }}
                />
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                {verificationFile ? (
                  <div>
                    <p className="font-medium text-gray-900">
                      {verificationFile.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {(verificationFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-gray-900">
                      Click to select PDF
                    </p>
                    <p className="text-sm text-gray-500">or drag and drop</p>
                  </div>
                )}
              </div>

              <Button
                className="w-full"
                onClick={handleVerifyDocument}
                disabled={!verificationFile || isVerifying}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Verify Signature
                  </>
                )}
              </Button>

              {/* Verification Result */}
              {verificationResult && (
                <div
                  className={`p-4 rounded-lg border ${getStatusColor(
                    verificationResult.status,
                  )}`}
                >
                  <div className="flex items-start gap-3">
                    {getStatusIcon(verificationResult.status)}
                    <div className="flex-1">
                      <p className="font-medium">
                        {verificationResult.message}
                      </p>
                      {verificationResult.signatures?.length > 0 && (
                        <div className="mt-3 space-y-3">
                          {verificationResult.signatures.map((sig) => (
                            <div
                              key={generateUUID()}
                              className="bg-white rounded-lg p-3 border"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {sig.signerName || "Unknown Signer"}
                                </span>
                                <Badge
                                  variant={
                                    sig.status === "valid"
                                      ? "default"
                                      : "destructive"
                                  }
                                  className={
                                    sig.status === "valid"
                                      ? "bg-green-100 text-green-700"
                                      : ""
                                  }
                                >
                                  {sig.status}
                                </Badge>
                              </div>
                              <div className="mt-2 text-sm space-y-1">
                                {sig.signedAt && (
                                  <p className="text-gray-600">
                                    Signed:{" "}
                                    {new Date(sig.signedAt).toLocaleString()}
                                  </p>
                                )}
                                {sig.reason && (
                                  <p className="text-gray-600">
                                    Reason: {sig.reason}
                                  </p>
                                )}
                                {sig.issuer && (
                                  <p className="text-gray-600">
                                    Issuer: {sig.issuer}
                                  </p>
                                )}
                                {sig.isSelfSigned && (
                                  <p className="text-yellow-600 text-xs">
                                    ⚠️ Self-signed certificate (not trusted by
                                    default)
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-3">
                        Verified at{" "}
                        {new Date(
                          verificationResult.verifiedAt,
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Certificate Dialog */}
      <GenerateCertificateDialog
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        onSubmit={(data) => generateMutation.mutate(data)}
        isLoading={generateMutation.isPending}
      />

      {/* Upload PEM Certificate Dialog */}
      <UploadCertificateDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onSubmit={(data) => uploadMutation.mutate(data)}
        isLoading={uploadMutation.isPending}
      />

      {/* Upload P12 Certificate Dialog */}
      <UploadP12Dialog
        open={showP12UploadDialog}
        onOpenChange={setShowP12UploadDialog}
        onSubmit={(formData) => uploadP12Mutation.mutate(formData)}
        isLoading={uploadP12Mutation.isPending}
      />

      {/* Revoke Certificate Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Certificate</DialogTitle>
            <DialogDescription>
              This certificate will no longer be used for signing. This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for revocation</Label>
              <Textarea
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="e.g., Key compromised, no longer needed, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRevokeDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                revokeMutation.mutate({
                  id: selectedCert?.id,
                  reason: revokeReason,
                })
              }
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke Certificate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate Details Dialog */}
      <CertificateDetailsDialog
        open={showCertDetailsDialog}
        onOpenChange={setShowCertDetailsDialog}
        certificate={selectedCert}
      />
    </div>
  );
};

// Generate Certificate Dialog Component
const GenerateCertificateDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    commonName: "",
    organisation: "",
    organisationalUnit: "",
    country: "",
    email: "",
    validityDays: 365,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Self-Signed Certificate</DialogTitle>
          <DialogDescription>
            Create a new certificate for signing documents. Self-signed
            certificates show as "untrusted" in Adobe until added to the trust
            store.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Common Name (CN) *</Label>
            <Input
              value={formData.commonName}
              onChange={(e) =>
                setFormData({ ...formData, commonName: e.target.value })
              }
              placeholder="Your Organization Name"
              required
            />
          </div>
          <div>
            <Label>Organization (O)</Label>
            <Input
              value={formData.organisation}
              onChange={(e) =>
                setFormData({ ...formData, organisation: e.target.value })
              }
              placeholder="Company Inc."
            />
          </div>
          <div>
            <Label>Organizational Unit (OU)</Label>
            <Input
              value={formData.organisationalUnit}
              onChange={(e) =>
                setFormData({ ...formData, organisationalUnit: e.target.value })
              }
              placeholder="Legal Department"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Country Code</Label>
              <Input
                value={formData.country}
                onChange={(e) =>
                  setFormData({ ...formData, country: e.target.value })
                }
                placeholder="US"
                maxLength={2}
              />
            </div>
            <div>
              <Label>Validity (days)</Label>
              <Input
                type="number"
                value={formData.validityDays}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    validityDays: parseInt(e.target.value),
                  })
                }
                min={1}
                max={3650}
              />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="signing@company.com"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !formData.commonName}>
              {isLoading ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Upload PEM Certificate Dialog
const UploadCertificateDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    certificatePem: "",
    privateKeyPem: "",
    caProvider: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload PEM Certificate</DialogTitle>
          <DialogDescription>
            Upload a CA-issued certificate and private key in PEM format.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Certificate (PEM) *</Label>
            <Textarea
              value={formData.certificatePem}
              onChange={(e) =>
                setFormData({ ...formData, certificatePem: e.target.value })
              }
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              rows={6}
              required
            />
          </div>
          <div>
            <Label>Private Key (PEM) *</Label>
            <Textarea
              value={formData.privateKeyPem}
              onChange={(e) =>
                setFormData({ ...formData, privateKeyPem: e.target.value })
              }
              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              rows={6}
              required
            />
          </div>
          <div>
            <Label>CA Provider</Label>
            <Input
              value={formData.caProvider}
              onChange={(e) =>
                setFormData({ ...formData, caProvider: e.target.value })
              }
              placeholder="e.g., DigiCert, GlobalSign"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isLoading || !formData.certificatePem || !formData.privateKeyPem
              }
            >
              {isLoading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Upload P12/PFX Dialog
const UploadP12Dialog = ({ open, onOpenChange, onSubmit, isLoading }) => {
  const [file, setFile] = useState(null);
  const [passphrase, setPassphrase] = useState("");
  const [caProvider, setCaProvider] = useState("");
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;

    const formData = new FormData();
    formData.append("certificate", file);
    formData.append("passphrase", passphrase);
    if (caProvider) formData.append("caProvider", caProvider);

    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload P12/PFX Certificate</DialogTitle>
          <DialogDescription>
            Upload a PKCS#12 (.p12 or .pfx) certificate file containing both the
            certificate and private key.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Certificate File *</Label>
            <div
              className="mt-2 border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-gray-300"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                aria-label="Upload Certificate File"
                type="file"
                accept=".p12,.pfx"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <div>
                  <FileKey className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600">
                    Click to select .p12 or .pfx file
                  </p>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label>Passphrase</Label>
            <Input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Leave empty if none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the passphrase used to protect the P12 file
            </p>
          </div>
          <div>
            <Label>CA Provider</Label>
            <Input
              value={caProvider}
              onChange={(e) => setCaProvider(e.target.value)}
              placeholder="e.g., DigiCert, GlobalSign"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !file}>
              {isLoading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Certificate Details Dialog
const CertificateDetailsDialog = ({ open, onOpenChange, certificate }) => {
  if (!certificate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Certificate Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-500">Common Name</Label>
              <p className="font-medium">{certificate.common_name}</p>
            </div>
            <div>
              <Label className="text-gray-500">Type</Label>
              <p className="font-medium capitalize">
                {certificate.certificate_type?.replace("_", " ")}
              </p>
            </div>
            <div>
              <Label className="text-gray-500">Organization</Label>
              <p className="font-medium">
                {certificate.organisation_name || "-"}
              </p>
            </div>
            <div>
              <Label className="text-gray-500">Issuer</Label>
              <p className="font-medium">
                {certificate.issuer_name || certificate.common_name}
              </p>
            </div>
            <div>
              <Label className="text-gray-500">Valid From</Label>
              <p className="font-medium">
                {new Date(certificate.valid_from).toLocaleDateString()}
              </p>
            </div>
            <div>
              <Label className="text-gray-500">Valid To</Label>
              <p className="font-medium">
                {new Date(certificate.valid_to).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div>
            <Label className="text-gray-500">Fingerprint (SHA-256)</Label>
            <p className="font-mono text-sm break-all bg-gray-50 p-2 rounded">
              {certificate.fingerprint}
            </p>
          </div>
          {certificate.ca_provider && (
            <div>
              <Label className="text-gray-500">CA Provider</Label>
              <p className="font-medium">{certificate.ca_provider}</p>
            </div>
          )}
          <div className="flex gap-2">
            {certificate.is_default && <Badge>Default</Badge>}
            {certificate.is_active && !certificate.revoked_at && (
              <Badge variant="outline" className="bg-green-50 text-green-700">
                Active
              </Badge>
            )}
            {certificate.revoked_at && (
              <Badge variant="destructive">Revoked</Badge>
            )}
            {certificate.isExpired && (
              <Badge variant="destructive">Expired</Badge>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CertificatesSettings;
